import type { MouseEvent } from 'react';
import { ExternalLink, FileText, StickyNote, Video } from 'lucide-react';
import type { AppPrompt } from './promptsV2Types';
import {
  docMetaFromUrl,
  hasPromptDescription,
  hasPromptDocument,
  hasPromptVideo,
  mediaUrl,
  overlayClose,
} from './promptsV2Utils';

export type PromptViewModalProps = {
  prompt: AppPrompt | null;
  onClose: () => void;
};

export function PromptViewModal({ prompt, onClose }: PromptViewModalProps) {
  if (!prompt) return null;

  const videoUrl = hasPromptVideo(prompt) ? mediaUrl(prompt.fileUrl) : '';
  const description = (prompt.description ?? '').trim();
  const docUrl = prompt.supportedDocumentUrl?.trim() ?? '';
  const docMeta = docUrl ? docMetaFromUrl(docUrl) : null;
  const docHref = docUrl ? mediaUrl(docUrl) : '';

  const hasDescription = hasPromptDescription(prompt);
  const hasDoc = hasPromptDocument(prompt);
  const hasVideo = Boolean(videoUrl);

  return (
    <div
      className="pv2-modal-backdrop bg-black/70"
      onMouseDown={(e: MouseEvent<HTMLDivElement>) => overlayClose(e, onClose)}
    >
      <div className="pv2-modal-backdrop__center">
        <div
          className="pv2-modal-panel pv2-modal-panel--lg flex max-h-[min(90vh,calc(100dvh-5.5rem))] flex-col overflow-hidden p-0"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <div className="min-w-0 pr-2">
              <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">
                Screening response
              </p>
              <h3 className="mt-1 text-base font-semibold leading-snug text-slate-950">
                {prompt.content}
              </h3>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              onClick={onClose}
            >
              Close
            </button>
          </div>

          <div className="space-y-4 overflow-y-auto px-5 py-4">
            {hasVideo ? (
              <section>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                  <Video size={14} className="text-emerald-700" />
                  Video
                </div>
                <video
                  className="max-h-[45vh] w-full rounded-xl border border-slate-200 bg-black object-contain"
                  controls
                  playsInline
                  src={videoUrl}
                >
                  <track kind="captions" />
                </video>
              </section>
            ) : null}

            {hasDescription ? (
              <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800">
                  <StickyNote size={16} className="text-emerald-700" />
                  Description
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {description}
                </p>
              </section>
            ) : null}

            {hasDoc && docHref ? (
              <section className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800">
                  <FileText size={16} className="text-emerald-700" />
                  Supporting document
                </div>
                <a
                  href={docHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100"
                >
                  <span className="text-base" aria-hidden>
                    {docMeta?.icon ?? '📎'}
                  </span>
                  <span className="truncate">{docMeta?.name ?? 'Open document'}</span>
                  {docMeta ? (
                    <span className="shrink-0 text-xs font-medium text-emerald-600">
                      {docMeta.type}
                    </span>
                  ) : null}
                  <ExternalLink size={14} className="shrink-0 opacity-70" />
                  <span className="sr-only">(opens in new tab)</span>
                </a>
              </section>
            ) : null}

            {!hasVideo && !hasDescription && !hasDoc ? (
              <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                No video, description, or supporting document yet. Use Edit to add content to this
                response.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
